import {
  type NavigationTarget,
  type PageSection,
  getPageSectionDefinition,
  isNavigationTarget,
  isPageSection,
  pageTargetRegistry
} from './page-targets';

export const PENDING_PAGE_INTERACTION_KEY =
  'ipma-copilot-pending-page-interaction';
export const PAGE_HIGHLIGHT_CLASS = 'ipma-copilot-page-highlight';

const PENDING_ACTION_VERSION = 1;
const PENDING_ACTION_TTL_MS = 30_000;
const SECTION_WAIT_TIMEOUT_MS = 12_000;
const HIGHLIGHT_DURATION_MS = 2_000;
const IPMA_HOSTS = new Set(['ipma.world', 'www.ipma.world']);
const highlightTimers = new WeakMap<HTMLElement, number>();

export interface PendingPageInteraction {
  version: typeof PENDING_ACTION_VERSION;
  id: string;
  target: NavigationTarget;
  section: PageSection;
  createdAt: number;
}

interface ResumeOptions {
  currentUrl?: string;
  root?: Document;
  signal?: AbortSignal;
  storage?: Storage;
}

const normalizePathname = (pathname: string) => {
  const normalized = pathname.replace(/\/+$/, '');
  return normalized || '/';
};

const normalizeText = (value: string | null) =>
  (value || '').replace(/\s+/g, ' ').trim().toLocaleLowerCase();

export const isCurrentPageTarget = (
  target: NavigationTarget,
  currentUrl = window.location.href
): boolean => {
  const current = new URL(currentUrl);
  const destination = new URL(pageTargetRegistry[target].url);

  return (
    IPMA_HOSTS.has(current.hostname.toLocaleLowerCase()) &&
    normalizePathname(current.pathname) ===
      normalizePathname(destination.pathname)
  );
};

export const findPageSection = (
  target: NavigationTarget,
  section: PageSection,
  root: Document = document
): HTMLElement | null => {
  const definition = getPageSectionDefinition(target, section);
  if (!definition) return null;

  if (definition.locator.kind === 'selector') {
    return root.querySelector<HTMLElement>(definition.locator.selector);
  }

  const expectedText = normalizeText(definition.locator.text);
  const heading = Array.from(
    root.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6')
  ).find((candidate) => normalizeText(candidate.textContent) === expectedText);

  if (!heading) return null;
  if (!definition.locator.closest) return heading;

  return heading.closest<HTMLElement>(definition.locator.closest) || heading;
};

export const waitForPageSection = (
  target: NavigationTarget,
  section: PageSection,
  root: Document = document,
  signal?: AbortSignal
): Promise<HTMLElement | null> => {
  const existing = findPageSection(target, section, root);
  if (existing) return Promise.resolve(existing);
  if (signal?.aborted) return Promise.resolve(null);

  return new Promise((resolve) => {
    const view = root.defaultView || window;
    let settled = false;

    const finish = (element: HTMLElement | null) => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      view.clearTimeout(timeoutId);
      root.removeEventListener('DOMContentLoaded', check);
      signal?.removeEventListener('abort', onAbort);
      resolve(element);
    };

    const check = () => {
      const element = findPageSection(target, section, root);
      if (element) finish(element);
    };

    const onAbort = () => finish(null);
    const observer = new view.MutationObserver(check);
    observer.observe(root.documentElement, {
      childList: true,
      subtree: true
    });
    root.addEventListener('DOMContentLoaded', check);
    signal?.addEventListener('abort', onAbort, { once: true });

    // The observer, rather than this timeout, decides when the DOM is ready.
    // The timeout only prevents a removed or renamed page section from leaving
    // a pending interaction alive indefinitely.
    const timeoutId = view.setTimeout(
      () => finish(null),
      SECTION_WAIT_TIMEOUT_MS
    );
  });
};

const ensureHighlightStyles = (root: Document) => {
  if (root.getElementById('ipma-copilot-page-interaction-styles')) return;

  const style = root.createElement('style');
  style.id = 'ipma-copilot-page-interaction-styles';
  style.textContent = `
    .${PAGE_HIGHLIGHT_CLASS} {
      outline: 2px solid rgba(237, 28, 36, 0.55) !important;
      outline-offset: 5px;
      background-color: rgba(237, 28, 36, 0.045) !important;
      transition: outline-color 180ms ease, background-color 180ms ease !important;
    }

    @media (prefers-reduced-motion: reduce) {
      .${PAGE_HIGHLIGHT_CLASS} {
        transition: none !important;
      }
    }
  `;
  root.head.appendChild(style);
};

const isFocusable = (element: HTMLElement) =>
  element.matches(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );

export const scrollToAndHighlight = (
  element: HTMLElement,
  root: Document = document
) => {
  const view = root.defaultView || window;
  const reducedMotion = view.matchMedia?.(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  ensureHighlightStyles(root);
  element.scrollIntoView({
    behavior: reducedMotion ? 'auto' : 'smooth',
    block: 'center',
    inline: 'nearest'
  });

  if (isFocusable(element)) {
    element.focus({ preventScroll: true });
  }

  const previousTimer = highlightTimers.get(element);
  if (previousTimer) view.clearTimeout(previousTimer);

  element.classList.add(PAGE_HIGHLIGHT_CLASS);
  const timer = view.setTimeout(() => {
    element.classList.remove(PAGE_HIGHLIGHT_CLASS);
    highlightTimers.delete(element);
  }, HIGHLIGHT_DURATION_MS);
  highlightTimers.set(element, timer);
};

export const focusPageSection = async (
  target: NavigationTarget,
  section: PageSection,
  root: Document = document,
  signal?: AbortSignal
): Promise<boolean> => {
  const element = await waitForPageSection(target, section, root, signal);
  if (!element || signal?.aborted) return false;

  scrollToAndHighlight(element, root);
  return true;
};

const parsePendingPageInteraction = (
  value: unknown,
  now = Date.now()
): PendingPageInteraction | null => {
  if (!value || typeof value !== 'object') return null;

  const candidate = value as Record<string, unknown>;
  const { version, id, target, section, createdAt } = candidate;
  if (
    version !== PENDING_ACTION_VERSION ||
    typeof id !== 'string' ||
    !id ||
    !isNavigationTarget(target) ||
    !isPageSection(target, section) ||
    typeof createdAt !== 'number' ||
    !Number.isFinite(createdAt) ||
    now - createdAt > PENDING_ACTION_TTL_MS ||
    createdAt > now + 5_000
  ) {
    return null;
  }

  return { version, id, target, section, createdAt };
};

export const readPendingPageInteraction = (
  storage: Storage = sessionStorage,
  now = Date.now()
): PendingPageInteraction | null => {
  try {
    const rawValue = storage.getItem(PENDING_PAGE_INTERACTION_KEY);
    if (!rawValue) return null;

    const pending = parsePendingPageInteraction(JSON.parse(rawValue), now);
    if (!pending) storage.removeItem(PENDING_PAGE_INTERACTION_KEY);
    return pending;
  } catch {
    try {
      storage.removeItem(PENDING_PAGE_INTERACTION_KEY);
    } catch {
      // Storage may be disabled; there is nothing else to clean up.
    }
    return null;
  }
};

export const storePendingPageInteraction = (
  action: Pick<PendingPageInteraction, 'id' | 'target' | 'section'>,
  storage: Storage = sessionStorage,
  now = Date.now()
): boolean => {
  if (!isPageSection(action.target, action.section)) return false;

  try {
    storage.setItem(
      PENDING_PAGE_INTERACTION_KEY,
      JSON.stringify({
        version: PENDING_ACTION_VERSION,
        id: action.id,
        target: action.target,
        section: action.section,
        createdAt: now
      } satisfies PendingPageInteraction)
    );
    return true;
  } catch {
    return false;
  }
};

export const clearPendingPageInteraction = (
  storage: Storage = sessionStorage
) => {
  try {
    storage.removeItem(PENDING_PAGE_INTERACTION_KEY);
  } catch {
    // Storage may be disabled.
  }
};

export const resumePendingPageInteraction = async ({
  currentUrl = window.location.href,
  root = document,
  signal,
  storage = sessionStorage
}: ResumeOptions = {}): Promise<boolean> => {
  const pending = readPendingPageInteraction(storage);
  if (!pending || !isCurrentPageTarget(pending.target, currentUrl)) {
    return false;
  }

  const element = await waitForPageSection(
    pending.target,
    pending.section,
    root,
    signal
  );
  if (signal?.aborted) return false;

  // Clear before changing the page so reload/back/forward cannot replay it.
  clearPendingPageInteraction(storage);
  if (!element) return false;

  scrollToAndHighlight(element, root);
  return true;
};
